<template>
  <div class="view-container">
    <div class="mail-layout">
      <div class="folder-panel">
        <div class="panel-header"><button @click="$emit('navigate', 'mailCompose')" class="btn btn-primary btn-sm" style="width:100%"><i class="fas fa-pen"></i> 写邮件</button></div>
        <div class="folder-list">
          <div :class="['folder-item', { active: folder === 'inbox' }]" @click="switchFolder('inbox')">
            <i class="fas fa-inbox"></i><span>收件箱</span><span class="folder-count">{{ unreadCount > 0 ? unreadCount : inboxCount }}</span>
          </div>
          <div :class="['folder-item', { active: folder === 'sent' }]" @click="switchFolder('sent')">
            <i class="fas fa-paper-plane"></i><span>已发送</span><span class="folder-count">{{ sentCount }}</span>
          </div>
          <div :class="['folder-item', { active: folder === 'drafts' }]" @click="switchFolder('drafts')">
            <i class="fas fa-file-alt"></i><span>草稿箱</span><span class="folder-count">{{ draftCount }}</span>
          </div>
          <div :class="['folder-item', { active: folder === 'trash' }]" @click="switchFolder('trash')">
            <i class="fas fa-trash"></i><span>已删除</span><span class="folder-count">{{ trashCount }}</span>
          </div>
        </div>
      </div>

      <div class="mail-list-panel">
        <div class="list-header">
          <div class="search-box"><input v-model="searchQuery" class="form-control form-control-sm" placeholder="搜索邮件..."></div>
        </div>
        <div class="mail-list">
          <div v-for="m in filteredMails" :key="m.id" :class="['mail-item', { unread: !m.read && folder === 'inbox', selected: selectedMail && selectedMail.id === m.id }]" @click="openMailDetail(m)">
            <div class="mail-avatar" :style="{ background: m.avatarColor }">{{ m.avatar }}</div>
            <div class="mail-info">
              <div class="mail-top"><span class="mail-from">{{ folder === 'sent' ? '发给: ' + m.toName : m.fromName }}</span><span class="mail-time">{{ m.time }}</span></div>
              <div class="mail-subject">{{ m.subject }}</div>
              <div class="mail-preview">{{ m.preview }}</div>
            </div>
            <span v-if="!m.read && folder === 'inbox'" class="unread-dot"></span>
          </div>
          <div v-if="filteredMails.length === 0" class="empty-list">暂无邮件</div>
        </div>
      </div>

      <div class="mail-detail-panel" v-if="selectedMail">
        <div class="detail-header">
          <h4>{{ selectedMail.subject }}</h4>
          <button @click="selectedMail = null" class="close-btn"><i class="fas fa-times"></i></button>
        </div>
        <div class="detail-meta">
          <div class="avatar large" :style="{ background: selectedMail.avatarColor }">{{ selectedMail.avatar }}</div>
          <div class="meta-info">
            <div class="meta-row"><label>发件人: </label><span>{{ selectedMail.from }} ({{ selectedMail.fromName }})</span></div>
            <div class="meta-row"><label>收件人: </label><span>{{ selectedMail.to }}</span></div>
            <div class="meta-row"><label>时间: </label><span>{{ selectedMail.fullTime }}</span></div>
            <div v-if="selectedMail.attachments && selectedMail.attachments.length > 0" class="meta-row"><label>附件: </label><span class="attach-list"><span v-for="a in selectedMail.attachments" :key="a.name" class="attach-item"><i class="fas fa-paperclip"></i> {{ a.name }} ({{ a.size }})</span></span></div>
          </div>
        </div>
        <div class="detail-body" v-html="selectedMail.body"></div>
        <div class="detail-actions">
          <button @click="replyMail(selectedMail)" class="btn btn-primary btn-sm"><i class="fas fa-reply"></i> 回复</button>
          <button @click="forwardMail(selectedMail)" class="btn btn-default btn-sm"><i class="fas fa-share"></i> 转发</button>
          <button @click="deleteMail(selectedMail)" class="btn btn-danger btn-sm"><i class="fas fa-trash"></i> 删除</button>
        </div>
      </div>
      <div v-else class="mail-detail-panel empty-detail">
        <div class="empty-state"><i class="fas fa-envelope-open-text"></i><p>选择一封邮件查看详情</p></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getInboxMails, getSentMails, getDraftMails, getTrashMails, getMailDetail, markMailsAsRead, deleteMails } from '../api/mail'

const emit = defineEmits(['navigate'])
const folder = ref('inbox')
const searchQuery = ref('')
const selectedMail = ref(null)
const loading = ref(false)

const inboxMails = ref([])
const sentMails = ref([])
const drafts = ref([])
const trash = ref([])

const inboxCount = ref(0)
const sentCount = ref(0)
const draftCount = ref(0)
const trashCount = ref(0)
const unreadCount = ref(0)

const currentMails = computed(() => {
  switch (folder.value) {
    case 'inbox': return inboxMails.value
    case 'sent': return sentMails.value
    case 'drafts': return drafts.value
    case 'trash': return trash.value
    default: return inboxMails.value
  }
})

const filteredMails = computed(() => {
  if (!searchQuery.value) return currentMails.value
  const q = searchQuery.value.toLowerCase()
  return currentMails.value.filter(m => 
    m.subject.toLowerCase().includes(q) || 
    m.fromName.toLowerCase().includes(q) || 
    m.preview.toLowerCase().includes(q)
  )
})

/**
 * 加载收件箱
 */
const loadInboxMails = async () => {
  try {
    loading.value = true
    const response = await getInboxMails({ pageIndex: 1, pageSize: 50 })
    
    if (response.data) {
      unreadCount.value = response.data.unreadCount || 0
      inboxCount.value = response.data.total || 0
      
      inboxMails.value = (response.data.mails || []).map(mail => ({
        id: mail.Id,
        from: mail.FromAccount,
        fromName: mail.FromName || mail.FromAccount,
        to: mail.ToAccount,
        toName: mail.ToName || mail.ToAccount,
        avatar: getAvatar(mail.FromName || mail.FromAccount),
        avatarColor: getRandomColor(),
        subject: mail.Titles || '(无主题)',
        preview: getPreview(mail.Contents),
        time: formatTime(mail.CreateTime),
        fullTime: formatFullTime(mail.CreateTime),
        read: mail.IsRead === 1,
        body: mail.Contents || '',
        attachments: []
      }))
      
      console.log('✅ 收件箱加载成功:', inboxMails.value.length, '封邮件')
    }
  } catch (error) {
    console.error('❌ 加载收件箱失败:', error)
    $message.error('加载收件箱失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 加载已发送
 */
const loadSentMails = async () => {
  try {
    loading.value = true
    const response = await getSentMails({ pageIndex: 1, pageSize: 50 })
    
    if (response.data) {
      sentCount.value = response.data.total || 0
      
      sentMails.value = (response.data.mails || []).map(mail => ({
        id: mail.Id,
        from: mail.FromAccount,
        fromName: mail.FromName || mail.FromAccount,
        to: mail.ToAccount,
        toName: mail.ToName || mail.ToAccount,
        avatar: getAvatar(mail.ToName || mail.ToAccount),
        avatarColor: getRandomColor(),
        subject: mail.Titles || '(无主题)',
        preview: getPreview(mail.Contents),
        time: formatTime(mail.CreateTime),
        fullTime: formatFullTime(mail.CreateTime),
        read: true,
        body: mail.Contents || '',
        attachments: []
      }))
    }
  } catch (error) {
    console.error('加载已发送失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 加载草稿箱
 */
const loadDraftMails = async () => {
  try {
    loading.value = true
    const response = await getDraftMails({ pageIndex: 1, pageSize: 50 })
    
    if (response.data) {
      draftCount.value = response.data.total || 0
      
      drafts.value = (response.data.mails || []).map(mail => ({
        id: mail.Id,
        from: mail.FromAccount,
        fromName: mail.FromName || mail.FromAccount,
        to: mail.ToAccount,
        toName: mail.ToName || mail.ToAccount,
        avatar: getAvatar(mail.FromName || mail.FromAccount),
        avatarColor: getRandomColor(),
        subject: mail.Titles || '(无主题)',
        preview: getPreview(mail.Contents),
        time: formatTime(mail.CreateTime),
        fullTime: formatFullTime(mail.CreateTime),
        read: true,
        body: mail.Contents || '',
        attachments: []
      }))
    }
  } catch (error) {
    console.error('加载草稿箱失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 加载已删除
 */
const loadTrashMails = async () => {
  try {
    loading.value = true
    const response = await getTrashMails({ pageIndex: 1, pageSize: 50 })
    
    if (response.data) {
      trashCount.value = response.data.total || 0
      
      trash.value = (response.data.mails || []).map(mail => ({
        id: mail.Id,
        from: mail.FromAccount,
        fromName: mail.FromName || mail.FromAccount,
        to: mail.ToAccount,
        toName: mail.ToName || mail.ToAccount,
        avatar: getAvatar(mail.FromName || mail.FromAccount),
        avatarColor: getRandomColor(),
        subject: mail.Titles || '(无主题)',
        preview: getPreview(mail.Contents),
        time: formatTime(mail.CreateTime),
        fullTime: formatFullTime(mail.CreateTime),
        read: mail.IsRead === 1,
        body: mail.Contents || '',
        attachments: []
      }))
    }
  } catch (error) {
    console.error('加载已删除失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 切换文件夹
 */
const switchFolder = async (folderName) => {
  folder.value = folderName
  selectedMail.value = null
  
  switch (folderName) {
    case 'inbox':
      if (inboxMails.value.length === 0) await loadInboxMails()
      break
    case 'sent':
      if (sentMails.value.length === 0) await loadSentMails()
      break
    case 'drafts':
      if (drafts.value.length === 0) await loadDraftMails()
      break
    case 'trash':
      if (trash.value.length === 0) await loadTrashMails()
      break
  }
}

const openMailDetail = async (m) => {
  if (folder.value === 'inbox' && !m.read) {
    m.read = true
    // 标记为已读
    try {
      await markMailsAsRead(m.id.toString())
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch (error) {
      console.error('标记已读失败:', error)
    }
  }
  selectedMail.value = m
}

const replyMail = (m) => { 
  emit('navigate', 'mailCompose')
  // TODO: 传递回复数据
}

const forwardMail = (m) => { 
  emit('navigate', 'mailCompose')
  // TODO: 传递转发数据
}

const deleteMail = async (m) => {
  if (!await $confirm('确定删除该邮件？')) return
  
  try {
    loading.value = true
    await deleteMails(m.id.toString())
    
    // 从当前列表中移除
    if (folder.value === 'inbox') {
      inboxMails.value = inboxMails.value.filter(x => x.id !== m.id)
      inboxCount.value = Math.max(0, inboxCount.value - 1)
    } else if (folder.value === 'sent') {
      sentMails.value = sentMails.value.filter(x => x.id !== m.id)
      sentCount.value = Math.max(0, sentCount.value - 1)
    } else if (folder.value === 'drafts') {
      drafts.value = drafts.value.filter(x => x.id !== m.id)
      draftCount.value = Math.max(0, draftCount.value - 1)
    }
    
    selectedMail.value = null
    $message.success('删除成功')
    
    // 重新加载垃圾箱
    await loadTrashMails()
  } catch (error) {
    console.error('删除失败:', error)
    $message.error('删除失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 工具函数
 */
const getAvatar = (name) => {
  if (!name) return '?'
  return name.substring(0, 1)
}

const getRandomColor = () => {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4']
  return colors[Math.floor(Math.random() * colors.length)]
}

const getPreview = (content) => {
  if (!content) return ''
  const text = content.replace(/<[^>]*>/g, '')
  return text.length > 50 ? text.substring(0, 50) + '...' : text
}

const formatTime = (datetime) => {
  if (!datetime) return ''
  
  const d = new Date(datetime)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days === 1) return '昨天'
  if (days < 7) return `${days}天前`
  
  return d.toLocaleDateString('zh-CN')
}

const formatFullTime = (datetime) => {
  if (!datetime) return ''
  const d = new Date(datetime)
  return d.toLocaleString('zh-CN')
}

onMounted(() => {
  loadInboxMails()
})
</script>

<style scoped>
.view-container { background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden; max-height: calc(100vh - 110px); }
.mail-layout { display: flex; height: calc(100vh - 115px); }

.folder-panel { flex: 0 0 200px; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; background: #fafbfc; }
.panel-header { padding: 16px; }
.folder-list { flex: 1; padding: 4px 0; }
.folder-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; cursor: pointer; font-size: 13px; color: #374151; transition: all 0.15s; }
.folder-item:hover { background: #f3f4f6; }
.folder-item.active { background: #eff6ff; color: #2563eb; font-weight: 500; }
.folder-item i { width: 18px; text-align: center; font-size: 14px; }
.folder-count { margin-left: auto; background: #e5e7eb; color: #6b7280; padding: 1px 7px; border-radius: 10px; font-size: 11px; }

.mail-list-panel { flex: 0 0 320px; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; }
.list-header { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
.form-control-sm { width: 100%; padding: 6px 10px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 12px; box-sizing: border-box; }
.mail-list { flex: 1; overflow-y: auto; }

.mail-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-bottom: 1px solid #f9fafb; cursor: pointer; transition: background 0.15s; }
.mail-item:hover { background: #f8fafc; }
.mail-item.selected { background: #eff6ff; }
.mail-item.unread { background: #fffdf5; }
.mail-avatar { width: 36px; height: 36px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 13px; font-weight: 600; flex-shrink: 0; }
.mail-avatar.large { width: 56px; height: 56px; font-size: 20px; }
.mail-info { flex: 1; min-width: 0; }
.mail-top { display: flex; justify-content: space-between; margin-bottom: 2px; }
.mail-from { font-size: 12px; font-weight: 600; color: #1f2937; }
.mail-time { font-size: 10px; color: #9ca3af; }
.mail-subject { font-size: 12px; color: #374151; margin-bottom: 2px; }
.mail-preview { font-size: 11px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.unread-dot { width: 7px; height: 7px; border-radius: 50%; background: #f59e0b; flex-shrink: 0; }
.empty-list { text-align: center; padding: 40px; color: #9ca3af; font-size: 13px; }

.mail-detail-panel { flex: 1; display: flex; flex-direction: column; min-width: 0; overflow-y: auto; }
.empty-detail { align-items: center; justify-content: center; }
.empty-state { text-align: center; color: #d1d5db; }
.empty-state i { font-size: 48px; margin-bottom: 12px; }
.empty-state p { font-size: 14px; }

.detail-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; background: #fafbfc; }
.detail-header h4 { margin: 0; font-size: 16px; color: #1f2937; }
.close-btn { background: none; border: none; font-size: 20px; color: #9ca3af; cursor: pointer; }
.close-btn:hover { color: #374151; }
.detail-meta { display: flex; gap: 16px; padding: 16px 20px; border-bottom: 1px solid #f3f4f6; background: #f9fafb; }
.meta-info { flex: 1; }
.meta-row { padding: 3px 0; font-size: 12px; color: #6b7280; }
.meta-row label { font-weight: 500; color: #374151; }
.attach-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.attach-item { background: #f3f4f6; padding: 4px 10px; border-radius: 4px; font-size: 11px; color: #374151; }
.detail-body { padding: 20px; flex: 1; font-size: 13px; line-height: 1.8; color: #374151; }
.detail-actions { padding: 12px 20px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px; background: #fafbfc; }

.btn { padding: 6px 14px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-primary { background: #f59e0b; color: #fff; border-color: #f59e0b; }
.btn-primary:hover { background: #d97706; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }
.btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }
.btn-danger:hover { background: #dc2626; }
.btn-sm { padding: 5px 10px; font-size: 12px; }
</style>
